import messages from './messages'

const STORAGE_KEY = 'site_locale'
const SUPPORTED_LOCALES = ['zh-CN', 'en-US']
const DEFAULT_LOCALE = 'zh-CN'

function getByPath(obj, path) {
  if (!obj || !path) return undefined
  return path.split('.').reduce((result, key) => (result == null ? undefined : result[key]), obj)
}

function interpolate(text, params) {
  if (!params || typeof text !== 'string') return text
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    const value = params[key]
    return value == null ? match : String(value)
  })
}

class I18nCore {
  constructor() {
    this.messages = messages
    this.listeners = new Set()
    this.locale = this.resolveInitialLocale()
    this.observer = null
    this._installed = false
  }

  resolveInitialLocale() {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (SUPPORTED_LOCALES.includes(saved)) {
      return saved
    }
    const browserLang = (navigator.language || '').toLowerCase()
    return browserLang.startsWith('en') ? 'en-US' : DEFAULT_LOCALE
  }

  setLocale(locale) {
    if (!SUPPORTED_LOCALES.includes(locale) || locale === this.locale) {
      return
    }
    this.locale = locale
    localStorage.setItem(STORAGE_KEY, locale)
    this.refreshDocument()
    this.listeners.forEach((listener) => {
      try {
        listener(locale)
      } catch (error) {
        console.error('[i18n] listener error:', error)
      }
    })
  }

  onChange(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  t(key, params) {
    const localePack = this.messages[this.locale] || {}
    const fallbackPack = this.messages[DEFAULT_LOCALE] || {}
    const value = getByPath(localePack, key)
    const fallbackValue = getByPath(fallbackPack, key)
    const text = value != null ? value : fallbackValue != null ? fallbackValue : key
    return interpolate(text, params)
  }

  // Translate by original Chinese text when API only returns text labels.
  tt(text, params) {
    if (!text) return text
    const localePack = this.messages[this.locale] || {}
    const fallbackPack = this.messages[DEFAULT_LOCALE] || {}
    const map = localePack.textMap || {}
    const fallbackMap = fallbackPack.textMap || {}
    const translated = map[text] || fallbackMap[text] || text
    return interpolate(translated, params)
  }

  parseParams(raw) {
    if (!raw) return undefined
    try {
      return JSON.parse(raw)
    } catch (error) {
      return undefined
    }
  }

  applyTranslations(root) {
    if (!root || !root.querySelectorAll) return
    const nodes = root.querySelectorAll(
      '[data-i18n], [data-i18n-placeholder], [data-i18n-title], [data-i18n-aria-label]'
    )
    nodes.forEach((node) => {
      const params = this.parseParams(node.getAttribute('data-i18n-params'))
      const textKey = node.getAttribute('data-i18n')
      const placeholderKey = node.getAttribute('data-i18n-placeholder')
      const titleKey = node.getAttribute('data-i18n-title')
      const ariaKey = node.getAttribute('data-i18n-aria-label')

      if (textKey) node.textContent = this.t(textKey, params)
      if (placeholderKey) node.setAttribute('placeholder', this.t(placeholderKey, params))
      if (titleKey) node.setAttribute('title', this.t(titleKey, params))
      if (ariaKey) node.setAttribute('aria-label', this.t(ariaKey, params))
    })
  }

  refreshDocument() {
    if (typeof document === 'undefined') return
    this.applyTranslations(document)
  }

  observe() {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined' || this.observer) {
      return
    }
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            this.applyTranslations(node)
          }
        })
      })
    })
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  install(Vue) {
    if (this._installed) return
    this._installed = true
    Vue.prototype.$i18n = this
    Vue.prototype.$t = (key, params) => this.t(key, params)
    Vue.prototype.$tt = (text, params) => this.tt(text, params)
    Vue.mixin({
      data() {
        return {
          localeTick: 0
        }
      },
      created() {
        if (!this.$i18n || typeof this.$forceUpdate !== 'function') return
        this.__unsubscribeI18n__ = this.$i18n.onChange(() => {
          this.localeTick += 1
          this.$forceUpdate()
        })
      },
      mounted() {
        if (this.$el && this.$el.nodeType === 1) {
          this.$i18n.applyTranslations(this.$el)
        }
      },
      beforeDestroy() {
        if (this.__unsubscribeI18n__) {
          this.__unsubscribeI18n__()
          this.__unsubscribeI18n__ = null
        }
      }
    })
    setTimeout(() => {
      this.refreshDocument()
      this.observe()
    }, 0)
  }
}

const i18n = new I18nCore()

export default i18n
